
import { TripData, UserInput, DayItinerary, TimeSlot } from '../types';
import { BrandConfig, defaultBrand } from '../contexts/BrandContext';

// ─── HELPERS ───────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string): string => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(y, m - 1, d));
  } catch {
    return dateStr;
  }
};

const getDayDate = (startDate: string, dayNumber: number): string => {
  try {
    const [y, m, d] = startDate.split('-').map(Number);
    const date = new Date(y, m - 1, d + (dayNumber - 1));
    return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date);
  } catch {
    return `Day ${dayNumber}`;
  }
};

const escapeHtml = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const escapeCSV = (str: string): string => {
  if (!str) return '';
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// ─── EMAIL HTML GENERATOR ──────────────────────────────────────────────────────

export function generateEmailHTML(tripData: TripData, metadata: UserInput, userInput: UserInput, brand?: BrandConfig): string {
  const b = brand || defaultBrand;
  const destination = escapeHtml(metadata.destination || 'Your Destination');
  const clientName = escapeHtml(metadata.clientName || 'Valued Guest');
  const companionName = metadata.companionName ? escapeHtml(metadata.companionName) : null;
  const startDate = formatDate(metadata.startDate);
  const endDate = formatDate(metadata.endDate);
  const agencyName = metadata.agencyName ? escapeHtml(metadata.agencyName) : null;
  const agentName = metadata.agentName ? escapeHtml(metadata.agentName) : null;
  const agentTitle = metadata.agentTitle ? escapeHtml(metadata.agentTitle) : null;
  const agentEmail = metadata.agentEmail ? escapeHtml(metadata.agentEmail) : null;
  const agentPhone = metadata.agentPhone ? escapeHtml(metadata.agentPhone) : null;

  const travelerLine = companionName ? `${clientName} &amp; ${companionName}` : clientName;

  const renderTimeSlotHTML = (label: string, slot: TimeSlot): string => {
    const bookingLink = slot.bookingUrl
      ? `<a href="${escapeHtml(slot.bookingUrl)}" style="color: #D4AF37; text-decoration: none; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; border-bottom: 1px solid #D4AF37;">Reserve &rarr;</a>`
      : '';
    const costBadge = slot.estimatedCost
      ? `<span style="font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 10px; color: #666; background: #F5F0E8; padding: 2px 8px; border-radius: 2px;">${escapeHtml(slot.estimatedCost)}</span>`
      : '';

    return `
      <tr>
        <td style="padding: 16px 0; border-bottom: 1px solid #F0EBE3;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td style="width: 80px; vertical-align: top; padding-right: 16px;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #D4AF37; font-weight: 600;">${escapeHtml(label)}</span>
              </td>
              <td style="vertical-align: top;">
                <div style="margin-bottom: 4px;">
                  <span style="font-family: 'Playfair Display', Georgia, serif; font-size: 15px; color: #1A1A1A; font-weight: 600;">${escapeHtml(slot.activity)}</span>
                  ${costBadge ? `&nbsp;&nbsp;${costBadge}` : ''}
                </div>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #555; line-height: 1.6; margin: 4px 0 8px 0; font-style: italic;">${escapeHtml(slot.description)}</p>
                <div style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; color: #999; letter-spacing: 0.5px;">
                  ${slot.location ? `<span>${escapeHtml(slot.location)}</span>` : ''}
                  ${bookingLink ? `&nbsp;&nbsp;&middot;&nbsp;&nbsp;${bookingLink}` : ''}
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>`;
  };

  const renderDayHTML = (day: DayItinerary): string => {
    const dayDate = getDayDate(metadata.startDate, day.day);
    return `
    <!-- Day ${day.day} -->
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 32px;">
      <tr>
        <td style="padding: 24px 32px; background-color: #FAFAF7;">
          <!-- Day Header -->
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 16px;">
            <tr>
              <td>
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: #D4AF37;">DAY ${day.day} &mdash; ${escapeHtml(dayDate)}</span>
              </td>
            </tr>
            <tr>
              <td style="padding-top: 4px;">
                <h3 style="font-family: 'Playfair Display', Georgia, serif; font-size: 28px; color: #1A1A1A; margin: 0; letter-spacing: -0.5px; text-transform: uppercase; font-weight: 600;">${escapeHtml(day.moodTitle)}</h3>
              </td>
            </tr>
          </table>

          <!-- Hero Image -->
          ${day.imageUrl ? `<img src="${escapeHtml(day.imageUrl)}" alt="Day ${day.day}" style="width: 100%; height: auto; max-height: 300px; object-fit: cover; display: block; margin-bottom: 16px;" />` : ''}

          <!-- Time Slots -->
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            ${renderTimeSlotHTML('Morning', day.morning)}
            ${renderTimeSlotHTML('Afternoon', day.afternoon)}
            ${renderTimeSlotHTML('Evening', day.evening)}
          </table>

          <!-- The Divergence -->
          ${day.contingency ? `
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 16px;">
            <tr>
              <td style="padding: 16px 0; border-top: 1px dashed #E8E3DA;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999; font-weight: 600;">THE DIVERGENCE</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 14px; color: #1A1A1A; margin: 8px 0 4px; font-weight: 600;">${escapeHtml(day.contingency.activity)}</p>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #555; font-style: italic; line-height: 1.6; margin: 0;">${escapeHtml(day.contingency.description)}</p>
              </td>
            </tr>
          </table>` : ''}

          <!-- Concierge Notes -->
          ${day.conciergeNotes ? `
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 16px;">
            <tr>
              <td style="padding: 12px 16px; border-left: 2px solid #D4AF37; background-color: #F5F0E8;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #D4AF37; display: block; margin-bottom: 4px;">Concierge Notes</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 12px; color: #666; font-style: italic; margin: 0; line-height: 1.6;">${escapeHtml(day.conciergeNotes)}</p>
              </td>
            </tr>
          </table>` : ''}

          <!-- Attire & Transit -->
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 12px;">
            <tr>
              <td style="width: 50%;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #999;">Attire</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 11px; color: #555; margin: 2px 0 0 0;">${escapeHtml(day.attire)}</p>
              </td>
              <td style="width: 50%; text-align: right;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #999;">Transit</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 11px; color: #555; margin: 2px 0 0 0;">${escapeHtml(day.transit)}</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
  };

  // Build logistics section
  const logisticsHTML = tripData.logistics ? `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 32px;">
      <tr>
        <td style="padding: 24px 32px; border: 1px solid #E8E3DA;">
          <h3 style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: #D4AF37; margin: 0 0 20px 0;">Logistics</h3>

          <!-- Flight -->
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 16px;">
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #F0EBE3;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999;">Flight</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 14px; color: #1A1A1A; margin: 4px 0 2px 0; font-weight: 600;">${escapeHtml(tripData.logistics.flight.name)}</p>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 12px; color: #666; margin: 0; font-style: italic; line-height: 1.6;">${escapeHtml(tripData.logistics.flight.description)}</p>
                ${tripData.logistics.flight.priceRange ? `<p style="font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 11px; color: #888; margin: 4px 0 0 0;">${escapeHtml(tripData.logistics.flight.priceRange)}</p>` : ''}
                ${tripData.logistics.flight.bookingUrl ? `<a href="${escapeHtml(tripData.logistics.flight.bookingUrl)}" style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #D4AF37; text-decoration: none; border-bottom: 1px solid #D4AF37;">Book Flight &rarr;</a>` : ''}
              </td>
            </tr>
          </table>

          <!-- Hotel -->
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td style="padding: 12px 0;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999;">Accommodation</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 14px; color: #1A1A1A; margin: 4px 0 2px 0; font-weight: 600;">${escapeHtml(tripData.logistics.hotel.name)}</p>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 12px; color: #666; margin: 0; font-style: italic; line-height: 1.6;">${escapeHtml(tripData.logistics.hotel.description)}</p>
                ${tripData.logistics.hotel.priceRange ? `<p style="font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 11px; color: #888; margin: 4px 0 0 0;">${escapeHtml(tripData.logistics.hotel.priceRange)}</p>` : ''}
                ${tripData.logistics.hotel.bookingUrl ? `<a href="${escapeHtml(tripData.logistics.hotel.bookingUrl)}" style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #D4AF37; text-decoration: none; border-bottom: 1px solid #D4AF37;">Book Hotel &rarr;</a>` : ''}
              </td>
            </tr>
          </table>

          ${tripData.eSimRecommendation ? `
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td style="padding: 12px 0; border-top: 1px solid #F0EBE3;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999; font-weight: 600;">CONNECTIVITY</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 14px; color: #1A1A1A; margin: 8px 0 2px; font-weight: 600;">${escapeHtml(tripData.eSimRecommendation.provider)} — ${escapeHtml(tripData.eSimRecommendation.planName)}</p>
                <p style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #666; margin: 0;">${escapeHtml(tripData.eSimRecommendation.dataAllowance)} · ${escapeHtml(tripData.eSimRecommendation.validity)} · From ${escapeHtml(tripData.eSimRecommendation.price)}</p>
                ${tripData.eSimRecommendation.bookingUrl ? `<a href="${escapeHtml(tripData.eSimRecommendation.bookingUrl)}" style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; color: #D4AF37; text-decoration: none; border-bottom: 1px solid #D4AF37; letter-spacing: 0.5px;">Activate eSIM &rarr;</a>` : ''}
              </td>
            </tr>
          </table>` : ''}

          ${tripData.destinationInsights?.emergencyContacts ? `
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td style="padding: 12px 0; border-top: 1px solid #F0EBE3;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999; font-weight: 600;">EMERGENCY CONTACTS</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 12px; color: #555; margin: 8px 0 0; line-height: 1.6;">${tripData.destinationInsights.emergencyContacts.map((c: { label: string; number: string }) => escapeHtml(c.label) + ': ' + escapeHtml(c.number)).join(' · ')}</p>
              </td>
            </tr>
          </table>` : ''}

          ${tripData.packingList && tripData.packingList.length > 0 ? `
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td style="padding: 12px 0; border-top: 1px solid #F0EBE3;">
                <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #999; font-weight: 600;">PACKING ESSENTIALS</span>
                <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 12px; color: #555; margin: 8px 0 0; line-height: 1.8;">${tripData.packingList.slice(0, 8).map((item: any) => escapeHtml(item.item || item)).join(' · ')}</p>
              </td>
            </tr>
          </table>` : ''}
        </td>
      </tr>
    </table>` : '';

  // Agent business card footer
  const agentCardHTML = (agentName || agencyName) ? `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 8px;">
      <tr>
        <td style="padding: 16px 0; border-top: 1px solid #E8E3DA;">
          ${agentName ? `<p style="font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #1A1A1A; margin: 0; font-weight: 600;">${agentName}</p>` : ''}
          ${agentTitle ? `<p style="font-family: 'Playfair Display', Georgia, serif; font-size: 11px; color: #888; margin: 2px 0 0 0; font-style: italic; line-height: 1.6;">${agentTitle}</p>` : ''}
          ${agencyName ? `<p style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #D4AF37; margin: 6px 0 0 0;">${agencyName}</p>` : ''}
          ${agentEmail ? `<p style="font-family: 'Playfair Display', Georgia, serif; font-size: 11px; color: #666; margin: 4px 0 0 0;"><a href="mailto:${agentEmail}" style="color: #666; text-decoration: none;">${agentEmail}</a></p>` : ''}
          ${agentPhone ? `<p style="font-family: 'Playfair Display', Georgia, serif; font-size: 11px; color: #666; margin: 2px 0 0 0;">${agentPhone}</p>` : ''}
        </td>
      </tr>
    </table>` : '';

  // Welcome note
  const welcomeHTML = tripData.welcomeNote ? `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 32px;">
      <tr>
        <td style="padding: 20px 32px; background-color: #F5F0E8; border-left: 3px solid #D4AF37;">
          <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #444; line-height: 1.7; margin: 0; font-style: italic;">${escapeHtml(tripData.welcomeNote)}</p>
        </td>
      </tr>
    </table>` : '';

  // Assemble the full email
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Travel Itinerary — ${destination}</title>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400&display=swap" rel="stylesheet">
</head>
<body style="margin: 0; padding: 0; background-color: #F5F0E8; font-family: 'Playfair Display', Georgia, serif; -webkit-font-smoothing: antialiased;">

  <!-- Outer Wrapper -->
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F0E8;">
    <tr>
      <td align="center" style="padding: 40px 16px;">

        <!-- Inner Container -->
        <table cellpadding="0" cellspacing="0" border="0" width="640" style="max-width: 640px; background-color: #FFFFFF;">

          <!-- Header Bar -->
          <tr>
            <td style="background-color: #1A1A1A; padding: 48px 40px; text-align: center;">
              <h1 style="font-family: 'Playfair Display', Georgia, serif; font-size: 36px; color: #FFFFFF; margin: 0; letter-spacing: 4px; text-transform: uppercase; font-weight: 600;">${destination}</h1>
              <div style="width: 40px; height: 1px; background-color: #D4AF37; margin: 16px auto;"></div>
              <p style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color: #D4AF37; margin: 0;">${escapeHtml(startDate)} &mdash; ${escapeHtml(endDate)}</p>
              <p style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #888; margin: 16px 0 0 0;">Prepared exclusively for</p>
              <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 16px; color: #FFFFFF; margin: 4px 0 0 0; font-style: italic; line-height: 1.6;">${travelerLine}</p>
            </td>
          </tr>

          <!-- Body Content -->
          <tr>
            <td style="padding: 32px 24px;">

              <!-- Welcome Note -->
              ${welcomeHTML}

              <!-- Destination Summary -->
              ${tripData.destinationSummary ? `
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 32px;">
                <tr>
                  <td style="padding: 0 8px;">
                    <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 14px; color: #555; line-height: 1.7; margin: 0; text-align: center; font-style: italic;">${escapeHtml(tripData.destinationSummary)}</p>
                  </td>
                </tr>
              </table>` : ''}

              <!-- Logistics -->
              ${logisticsHTML}

              <!-- Divider -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 24px;">
                <tr>
                  <td style="text-align: center;">
                    <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 10px; letter-spacing: 4px; text-transform: uppercase; color: #D4AF37;">Daily Itinerary</span>
                    <div style="width: 60px; height: 1px; background-color: #D4AF37; margin: 12px auto 0;"></div>
                  </td>
                </tr>
              </table>

              <!-- Itinerary Days -->
              ${tripData.itinerary.map(day => renderDayHTML(day)).join('\n')}

              <!-- Weather -->
              ${tripData.weather ? `
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 24px;">
                <tr>
                  <td style="padding: 16px 24px; background-color: #FAFAF7; border: 1px solid #F0EBE3; text-align: center;">
                    <span style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #D4AF37;">Expected Weather</span>
                    <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #555; margin: 6px 0 0 0;">${escapeHtml(tripData.weather.condition)} &middot; ${escapeHtml(tripData.weather.temperatureRange)}</p>
                  </td>
                </tr>
              </table>` : ''}

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #1A1A1A; padding: 32px 40px; text-align: center;">
              ${agentCardHTML || `
              ${b.logo ? `<img src="${escapeHtml(b.logo)}" alt="${escapeHtml(b.name)}" style="width: 40px; height: 40px; border-radius: 50%; margin: 0 auto 8px; display: block; object-fit: cover;" />` : ''}
              <p style="font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: ${escapeHtml(b.primaryColor)}; margin: 0;">${escapeHtml(b.name)}</p>
              `}
              <div style="width: 30px; height: 1px; background-color: ${escapeHtml(b.primaryColor)}; margin: 16px auto;"></div>
              <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 10px; color: #666; margin: 0;">${escapeHtml(b.footerText)}</p>
              ${b.contactEmail ? `<p style="font-family: 'Playfair Display', Georgia, serif; font-size: 9px; color: #555; margin: 4px 0 0 0;"><a href="mailto:${escapeHtml(b.contactEmail)}" style="color: #555; text-decoration: none;">${escapeHtml(b.contactEmail)}</a></p>` : ''}
              <p style="font-family: 'Playfair Display', Georgia, serif; font-size: 9px; color: #555; margin: 8px 0 0 0; font-style: italic; line-height: 1.6;">This itinerary was generated by ${escapeHtml(b.name)}. All booking links and details should be verified before finalizing.</p>
            </td>
          </tr>

        </table>
        <!-- /Inner Container -->

      </td>
    </tr>
  </table>
  <!-- /Outer Wrapper -->

</body>
</html>`;
}

// ─── CSV EXPORT ────────────────────────────────────────────────────────────────

export function generateCSVExport(tripData: TripData, metadata: UserInput): string {
  const rows: string[] = [];

  // Header row
  rows.push('Day,Date,Time,Activity,Location,Description,Estimated Cost,Booking URL,Concierge Notes,Attire,Transit');

  tripData.itinerary.forEach((day: DayItinerary) => {
    const dayDate = getDayDate(metadata.startDate, day.day);
    const sections: { time: string; slot: TimeSlot }[] = [
      { time: 'Morning', slot: day.morning },
      { time: 'Afternoon', slot: day.afternoon },
      { time: 'Evening', slot: day.evening },
    ];

    sections.forEach(({ time, slot }, idx) => {
      rows.push([
        escapeCSV(String(day.day)),
        escapeCSV(dayDate),
        escapeCSV(time),
        escapeCSV(slot.activity),
        escapeCSV(slot.location),
        escapeCSV(slot.description),
        escapeCSV(slot.estimatedCost || ''),
        escapeCSV(slot.bookingUrl || ''),
        idx === 0 ? escapeCSV(day.conciergeNotes) : '',
        idx === 0 ? escapeCSV(day.attire) : '',
        idx === 0 ? escapeCSV(day.transit) : '',
      ].join(','));
    });
  });

  // Add logistics rows
  if (tripData.logistics) {
    rows.push('');
    rows.push('Logistics,,,,,,,,,');
    rows.push([
      'Flight',
      '',
      '',
      escapeCSV(tripData.logistics.flight.name),
      '',
      escapeCSV(tripData.logistics.flight.description),
      escapeCSV(tripData.logistics.flight.priceRange),
      escapeCSV(tripData.logistics.flight.bookingUrl || ''),
      '',
      '',
      '',
    ].join(','));
    rows.push([
      'Hotel',
      '',
      '',
      escapeCSV(tripData.logistics.hotel.name),
      '',
      escapeCSV(tripData.logistics.hotel.description),
      escapeCSV(tripData.logistics.hotel.priceRange),
      escapeCSV(tripData.logistics.hotel.bookingUrl || ''),
      '',
      '',
      '',
    ].join(','));
  }

  return rows.join('\n');
}

// ─── CLIPBOARD COPY ────────────────────────────────────────────────────────────

export async function copyEmailToClipboard(html: string): Promise<boolean> {
  try {
    // Try rich HTML clipboard write first
    const blob = new Blob([html], { type: 'text/html' });
    const clipboardItem = new ClipboardItem({ 'text/html': blob });
    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch {
    // Fallback: copy as plain text (strip HTML)
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      await navigator.clipboard.writeText(plainText);
      return true;
    } catch {
      return false;
    }
  }
}

// ─── CSV DOWNLOAD ──────────────────────────────────────────────────────────────

export function downloadAsCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── CONVENIENCE: TOAST HELPER (deprecated — use useToast() hook instead) ─────

import { getGlobalToast } from '../contexts/ToastContext';

export function showExportToast(message: string): void {
  const globalToast = getGlobalToast();
  if (globalToast) {
    const isError = message.toLowerCase().includes('fail') || message.toLowerCase().includes('could not');
    if (isError) {
      globalToast.error(message);
    } else {
      globalToast.success(message);
    }
  } else {
    // Fallback if toast context isn't available yet
    console.warn('[showExportToast] Toast context not available:', message);
  }
}

// ─── CALENDAR EXPORT (iCal / .ics) ──────────────────────────────────────────

const escapeICS = (str: string): string =>
  str.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

const toICSDate = (dateStr: string, dayNumber: number): string => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d + (dayNumber - 1));
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  } catch {
    return '';
  }
};

/** Build ICS content string for Apple Calendar / .ics download */
const buildICSContent = (tripData: TripData, userData: UserInput): string | null => {
  const startDate = userData.startDate;
  if (!startDate) return null;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Aura Traveler//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeICS(userData.destination)} Itinerary`,
  ];

  tripData.itinerary.forEach((day) => {
    const dateStr = toICSDate(startDate, day.day);
    if (!dateStr) return;
    const nextDateStr = toICSDate(startDate, day.day + 1);

    const description = [
      day.morning?.activity ? `Morning: ${day.morning.activity}` : '',
      day.afternoon?.activity ? `Afternoon: ${day.afternoon.activity}` : '',
      day.evening?.activity ? `Evening: ${day.evening.activity}` : '',
      day.conciergeNotes ? `\\nNotes: ${day.conciergeNotes}` : '',
    ].filter(Boolean).join('\\n');

    lines.push(
      'BEGIN:VEVENT',
      `DTSTART;VALUE=DATE:${dateStr}`,
      `DTEND;VALUE=DATE:${nextDateStr}`,
      `SUMMARY:${escapeICS(`Day ${day.day}: ${day.moodTitle}`)}`,
      `DESCRIPTION:${escapeICS(description)}`,
      `LOCATION:${escapeICS(userData.destination)}`,
      `UID:aura-${dateStr}-${day.day}@auratraveler.com`,
      'END:VEVENT',
    );
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
};

/** Download .ics file (Apple Calendar / any calendar app) */
export const generateCalendarExport = (tripData: TripData, userData: UserInput): void => {
  const ics = buildICSContent(tripData, userData);
  if (!ics) return;

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(userData.destination || 'trip').split(',')[0].trim()}-itinerary.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/** Open Google Calendar with all itinerary days as events */
export const generateGoogleCalendarExport = (tripData: TripData, userData: UserInput): void => {
  const startDate = userData.startDate;
  if (!startDate) return;

  // Google Calendar only supports adding one event at a time via URL,
  // so we open the first day and provide a combined description with all days
  const firstDay = tripData.itinerary[0];
  if (!firstDay) return;

  const lastDay = tripData.itinerary[tripData.itinerary.length - 1];
  const startICS = toICSDate(startDate, firstDay.day);
  const endICS = toICSDate(startDate, (lastDay?.day ?? firstDay.day) + 1);
  if (!startICS || !endICS) return;

  const allDaysDescription = tripData.itinerary.map((day) => {
    const parts = [
      `Day ${day.day}: ${day.moodTitle}`,
      day.morning?.activity ? `  Morning: ${day.morning.activity}` : '',
      day.afternoon?.activity ? `  Afternoon: ${day.afternoon.activity}` : '',
      day.evening?.activity ? `  Evening: ${day.evening.activity}` : '',
    ].filter(Boolean).join('\n');
    return parts;
  }).join('\n\n');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${userData.destination} Itinerary — Aura Traveler`,
    dates: `${startICS}/${endICS}`,
    details: allDaysDescription,
    location: userData.destination || '',
  });

  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
};
